(() => {
  class LovaQty extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-lova-qty-input]');
      this.minus = this.querySelector('[data-lova-qty-minus]');
      this.plus = this.querySelector('[data-lova-qty-plus]');
      this.min = Math.max(1, Number(this.input?.min || 1));
      this.max = this.input?.max ? Number(this.input.max) : Infinity;

      this.minus?.addEventListener('click', () => this.setValue(this.getValue() - 1));
      this.plus?.addEventListener('click', () => this.setValue(this.getValue() + 1));
      this.input?.addEventListener('change', () => this.setValue(this.getValue()));
    }

    getValue() {
      return Math.max(this.min, parseInt(this.input?.value, 10) || this.min);
    }

    setValue(next) {
      let value = Number(next);
      if (Number.isNaN(value)) value = this.min;
      value = Math.max(this.min, Math.min(this.max, value));
      if (this.input) this.input.value = String(value);
      this.minus?.toggleAttribute('disabled', value <= this.min);
      this.plus?.toggleAttribute('disabled', value >= this.max);
    }

    canAddToCart() {
      const value = this.getValue();
      return { canAdd: value >= this.min, maxQuantity: this.max === Infinity ? undefined : this.max };
    }
  }

  if (!customElements.get('lova-qty')) customElements.define('lova-qty', LovaQty);

  function closestProductRoot(node) {
    return node.closest('[data-lova-product]');
  }

  function parseJson(script) {
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  function variantMatches(variant, selected) {
    return variant.options.every((option, index) => option === selected[index]);
  }

  function initProduct(root) {
    if (root.dataset.lovaReady === 'true') return;
    root.dataset.lovaReady = 'true';

    const data = parseJson(root.querySelector('[data-lova-product-json]'));
    if (!data) return;

    const selected = [...(data.selectedOptions || [])];
    const variantInput = root.querySelector('input[name="id"]');
    const priceEl = root.querySelector('[data-lova-price]');
    const compareEl = root.querySelector('[data-lova-compare]');
    const atc = root.querySelector('[data-lova-atc]');
    const atcLabel = root.querySelector('[data-lova-atc-label]');
    const errorEl = root.querySelector('[data-lova-atc-error]');
    const optionGroups = [...root.querySelectorAll('[data-lova-option-group]')];
    const slides = [...root.querySelectorAll('[data-lova-slide]')];
    const thumbs = [...root.querySelectorAll('[data-lova-thumb]')];
    const dots = [...root.querySelectorAll('[data-lova-dot]')];
    const labels = {
      add: root.dataset.addLabel || 'Add to Bag',
      sold: root.dataset.soldLabel || 'Sold out',
      unavailable: root.dataset.unavailableLabel || 'Unavailable',
    };

    function currentVariant() {
      return data.variants.find((variant) => variantMatches(variant, selected)) || data.variants[0];
    }

    function setGalleryIndex(index) {
      const count = slides.length;
      if (!count) return;
      const next = ((index % count) + count) % count;

      slides.forEach((slide, i) => {
        slide.hidden = i !== next;
        slide.classList.toggle('is-active', i === next);
      });
      thumbs.forEach((thumb, i) => {
        const on = i === next;
        thumb.classList.toggle('is-active', on);
        thumb.setAttribute('aria-current', on ? 'true' : 'false');
      });
      dots.forEach((dot, i) => {
        const on = i === next;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-current', on ? 'true' : 'false');
      });
      root.dataset.galleryIndex = String(next);
    }

    function showMedia(mediaId) {
      if (!mediaId) return;
      const index = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
      if (index >= 0) setGalleryIndex(index);
    }

    function applyVariant(variant) {
      if (!variant || !variantInput) return;
      variantInput.value = String(variant.id);

      optionGroups.forEach((group, groupIndex) => {
        group.querySelectorAll('[data-lova-option]').forEach((button) => {
          const on = button.dataset.lovaOption === selected[groupIndex];
          button.classList.toggle('is-selected', on);
          button.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      });

      if (priceEl) priceEl.innerHTML = variant.price_html;
      if (compareEl) {
        const onSale = Boolean(variant.compare_html);
        compareEl.hidden = !onSale;
        compareEl.innerHTML = variant.compare_html || '';
      }

      const available = Boolean(variant.available);
      if (atc) atc.disabled = !available;
      if (atcLabel) atcLabel.textContent = available ? labels.add : labels.sold;

      showMedia(variant.featured_media_id);
    }

    optionGroups.forEach((group, groupIndex) => {
      group.querySelectorAll('[data-lova-option]').forEach((button) => {
        button.addEventListener('click', () => {
          selected[groupIndex] = button.dataset.lovaOption;
          const variant = currentVariant();
          if (!variant) {
            if (atc) atc.disabled = true;
            if (atcLabel) atcLabel.textContent = labels.unavailable;
            return;
          }
          applyVariant(variant);
        });
      });
    });

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => setGalleryIndex(index));
    });
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => setGalleryIndex(index));
    });

    const accordions = root.querySelectorAll('[data-lova-accordion]');
    accordions.forEach((item) => {
      const trigger = item.querySelector('[data-lova-accordion-trigger]');
      const panel = item.querySelector('[data-lova-accordion-panel]');
      if (!trigger || !panel) return;
      trigger.addEventListener('click', () => {
        const willOpen = !item.classList.contains('is-open');
        accordions.forEach((other) => {
          const otherTrigger = other.querySelector('[data-lova-accordion-trigger]');
          const otherPanel = other.querySelector('[data-lova-accordion-panel]');
          if (!otherTrigger || !otherPanel) return;
          const open = willOpen && other === item;
          other.classList.toggle('is-open', open);
          otherTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
          otherPanel.setAttribute('aria-hidden', open ? 'false' : 'true');
        });
      });
    });

    applyVariant(currentVariant());
    setGalleryIndex(0);

    const formComponent = root.querySelector('product-form-component');
    formComponent?.addEventListener(
      'shopify:cart:lines-update',
      (event) => {
        if (event.action && event.action !== 'add') return;
        const drawer = document.querySelector('theme-drawer#cart-drawer');
        event.promise
          ?.then((result) => {
            if (result?.detail?.didError) return;
            drawer?.open?.();
          })
          .catch(() => {});
      },
      true
    );

    if (errorEl) {
      document.addEventListener('cart:error', () => {
        errorEl.hidden = false;
      });
    }
  }

  function boot() {
    document.querySelectorAll('[data-lova-product]').forEach(initProduct);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-lova-product]').forEach(initProduct);
  });
})();
